import axios from "axios";
import {useQuoteStore} from "@/stores/QuoteStore";
import {useCoreStore} from "@/stores/CoreStore";
// import { useQuoteStore } from "@/stores/QuoteStore";

export default class QuoteBuilderService {
    constructor(pageData) {
        this.baseUrl = process.env.VUE_APP_API_URL
        this.pageData = pageData

        // Create Axios instance with base URL
        this.axios = axios.create({
            baseURL: this.baseUrl,
        });

    }

    // Quote Payment Methods
    
    /**
     * Get quote by payment token
     * @param {string} token - The payment token from URL parameter
     * @returns {Promise} Quote data
     */
    async getQuoteByToken(token) {
        try {
            return await this.axios.get(`/api/quote/token/${token}`)
        } catch (error) {
            console.error('Error fetching quote by token:', error)
            throw error
        }
    }

    /**
     * Get quote by reference, postcode and PIN code
     * @param {Object} data - Object containing reference, postcode and optional PIN code
     * @returns {Promise} Quote data
     */
    async getQuoteByReference(data) {
        try {
            return await this.axios.post('/api/quote/lookup', {
                job_number: data.reference,
                postcode: data.postcode,
                pin: data.pin
            })
        } catch (error) {
            console.error('Error fetching quote by reference:', error)
            throw error
        }
    }

    /**
     * Process quote payment
     * @param {Object} paymentData - Payment information including quoteId, billing details, and payment method
     * @returns {Promise} Payment confirmation
     */
    async processQuotePayment(paymentData) {
        try {
            const response = await this.axios.post('/api/quote-payment/process', paymentData)
            return response
        } catch (error) {
            console.error('Error processing quote payment:', error)
            throw error
        }
    }

    /**
     * Submit a quote as "pay on account" (credit / add to account).
     * NOTE: Endpoint may need to be adjusted to match the backend implementation.
     * @param {string} token - The quote payment token
     * @param {Object} payload - Checkout payload (customer + address details)
     * @returns {Promise} Pending order confirmation
     */
    async submitQuoteToAccount(token, payload) {
        try {
            return await this.axios.post(`/api/quote/checkout/${token}`, payload)
        } catch (error) {
            console.error('Error submitting quote to account:', error)
            throw error
        }
    }

    /**
     * Request amendments to a quote.
     * NOTE: Endpoint may need to be adjusted to match the backend implementation.
     * @param {string} token - The quote payment token
     * @param {Object} payload - { quote_id, amendments: [{ type, code, item_id, notes }] }
     * @returns {Promise} Amendment request confirmation
     */
    async requestQuoteAmendments(token, payload) {
        try {
            return await this.axios.post(`/api/quote/checkout/${token}/amendments`, payload)
        } catch (error) {
            console.error('Error requesting quote amendments:', error)
            throw error
        }
    }

    /**
     * Validate quote for payment
     * @param {string} quoteId - The quote ID to validate
     * @returns {Promise} Validation result
     */
    async validateQuoteForPayment(quoteId) {
        try {
            const response = await this.axios.get(`/api/quote-payment/validate/${quoteId}`)
            return response
        } catch (error) {
            console.error('Error validating quote:', error)
            throw error
        }
    }

    // Super Payments Methods

    /**
     * Get Super Payments checkout session
     * @param {string} token - The quote payment token
     * @returns {Promise} Checkout session with checkoutSessionToken and checkoutSessionId
     */
    async getSuperCheckoutSession(token) {
        try {
            return await this.axios.get(`/api/super/show/${token}`)
        } catch (error) {
            console.error('Error fetching Super checkout session:', error)
            throw error
        }
    }

    /**
     * Proceed with Super Payments checkout
     * @param {string} token - The quote payment token
     * @param {Object} payload - Payment details (amount, customer info, addresses, urls)
     * @returns {Promise} Proceed response with redirectUrl
     */
    async proceedSuperPayment(token, payload) {
        try {
            return await this.axios.post(`/api/super/proceed/${token}`, payload)
        } catch (error) {
            console.error('Error proceeding with Super payment:', error)
            throw error
        }
    }

    /**
     * Apply a voucher code to a quote
     * @param {string} token - The quote payment token
     * @param {string} voucherCode - The voucher code
     * @returns {Promise} Updated quote data
     */
    async applyVoucher(token, voucherCode) {
        try {
            return await this.axios.post(`/api/quote/checkout/${token}/voucher`, { voucher_code: voucherCode })
        } catch (error) {
            console.error('Error applying voucher:', error)
            throw error
        }
    }

    /**
     * Remove a voucher code from a quote
     * @param {string} token - The quote payment token
     * @returns {Promise} Updated quote data
     */
    async removeVoucher(token) {
        try {
            return await this.axios.delete(`/api/quote/checkout/${token}/voucher`)
        } catch (error) {
            console.error('Error removing voucher:', error)
            throw error
        }
    }
}
